import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import config from 'config'
import { NavigationGuardProvider } from 'next-navigation-guard'
import App, { AppContext } from 'next/app'
import Head from 'next/head'
import Router from 'next/router'
import NProgress from 'nprogress'
import * as R from 'ramda'
import { useSelector } from 'react-redux'
import { install } from 'resize-observer'

import 'src/styles/global.css'
import 'src/styles/index.scss'

import 'tippy.js/animations/scale.css'
import 'tippy.js/dist/tippy.css'

import { BannerCalloutProvider } from 'src/components/banner-callout/banner-callout-context'
import ErrorBoundary from 'src/components/error-boundary'
import Banner from 'src/components/generics/banner'
import ReduxModal from 'src/components/generics/modal/redux'
import SnackBar from 'src/components/generics/snackbar'
import InterceptPage from 'src/components/interception'
import { DataAccessLayer } from 'src/data-access-layer'
import { ViewportProvider } from 'src/hooks/useViewport'
import { AppAnalytics } from 'src/lib/analytics/app-analytics'
import { initializeApollo } from 'src/lib/apollo'
import graph2 from 'src/lib/data/graph2'
import { initDatadog } from 'src/lib/datadog/init-datadog'
import {
  FeatureFlags,
  FeatureFlagsProvider,
  fetchFeatureFlags
} from 'src/lib/feature-flags'
import { treeToDb } from 'src/lib/graph/walk'
import { newNetworkRedirect } from 'src/lib/router'
import { isValidSquuid } from 'src/lib/util/board'
import { isBrowser } from 'src/lib/util/dom'
import { path } from 'src/lib/util/url'
import { getErrorInterceptionSelector } from 'src/store/errors/selectors'
import {
  CustomAppProps,
  NetworkAppContextType,
  NetworkPageContext,
  YdAppProps
} from 'src/types/next'
import logger from 'util/logger'
import { getSession } from 'util/session'

import { baseUserFragment } from '../src/lib/data/graphFragments'
import {
  getNetworkSubdomain,
  handleApolloError,
  handleErrorData
} from '../src/lib/middleware'
import { OnlineUserContextProvider } from 'src/lib/online-user-context'

if (typeof window !== 'undefined') {
  install()
}

if (isBrowser) {
  NProgress.configure({ showSpinner: false })
}

initDatadog()

function setupGA(pageProps: YdAppProps) {
  if (config.NEXT_PUBLIC_ENABLE_GA === '1' && isBrowser) {
    window.gtag('config', 'UA-121446211-1', {
      user_id: R.path(['user', ':db/id'], pageProps)
    })
  }
}

function registerRouterEvents() {
  Router.events.on('routeChangeStart', NProgress.start)
  Router.events.on('routeChangeComplete', NProgress.done)
  Router.events.on('routeChangeError', NProgress.done)
}

function YDApp({
  Component,
  pageProps
}: CustomAppProps<NetworkPageContext, YdAppProps, YdAppProps>) {
  registerRouterEvents()
  setupGA(pageProps)

  // pageProps.interception = {
  //   error: 'user-blocked-from-board'
  // }

  const Main = () => {
    const { interception } = pageProps
    const errorInterception = useSelector(getErrorInterceptionSelector)

    if (interception) {
      const interceptLayout = InterceptPage.getLayout ?? R.identity

      if (interception.error.notAllowed || interception.error.notFound) {
        const errorProps = {
          error: interception.error.notFound ? 'not-found' : 'not-allowed'
        }
        return interceptLayout(<InterceptPage {...errorProps} />)
      }

      const interceptedError =
        R.prop('error', interception) || R.prop('error', errorInterception)
      const interceptedData =
        R.prop('data', interception) || R.prop('data', errorInterception)

      return interceptLayout(
        <InterceptPage error={interceptedError} data={interceptedData} />
      )
    }

    const getLayout = Component.getLayout ?? R.identity
    return getLayout(<Component {...pageProps} />)
  }

  return (
    <AppAnalytics>
      <OnlineUserContextProvider>
        <FeatureFlagsProvider ff={pageProps.flags}>
          <NavigationGuardProvider>
            <Head>
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1"
              />
            </Head>
            <DataAccessLayer pageProps={pageProps}>
              <ErrorBoundary>
                <ViewportProvider>
                  <BannerCalloutProvider>
                    <Main />
                  </BannerCalloutProvider>
                </ViewportProvider>
              </ErrorBoundary>
              <Banner />
              <SnackBar />
              <ReduxModal />
            </DataAccessLayer>
            <div
              id="yd-portal__announcements"
              className="sr-only"
              aria-live="polite"
              aria-atomic="true"
            />
            <div
              id="yd-portal__announcements--assertive"
              className="sr-only"
              aria-live="assertive"
              aria-atomic="true"
            />
          </NavigationGuardProvider>
        </FeatureFlagsProvider>
      </OnlineUserContextProvider>
    </AppAnalytics>
  )
}

async function getInitialProps(
  ctx: AppContext,
  client: ApolloClient<NormalizedCacheObject>
) {
  let props = { pageProps: {} }
  const networkName = getNetworkSubdomain(ctx.ctx)
  const networkCtx: NetworkAppContextType = {
    ...ctx,
    ctx: {
      ...ctx.ctx,
      ...{ networkName, apolloClient: client }
    }
  }

  const notNetworkPaths = [
    '/user/verification',
    'user/emails',
    '/user/login',
    '/user/find-organization'
  ]

  if (
    !notNetworkPaths.includes(ctx.ctx.pathname) &&
    config.NEXT_PUBLIC_YD_STAGE !== 'preview' &&
    newNetworkRedirect(ctx)
  ) {
    return
  }

  const unverifiedPaths = [
    '/organization/invitation',
    '/community/[id]/invitation',
    '/n/[network]/organization/invitation',
    '/n/[network]/community/[id]/invitation',
    ...notNetworkPaths
  ]

  if (
    !unverifiedPaths.includes(ctx.ctx.pathname) &&
    getSession(ctx.ctx.req) &&
    networkCtx.ctx.networkName
  ) {
    const graphQuery = baseUserFragment(networkCtx.ctx, {})
    const data = await graph2(networkCtx.ctx.req, graphQuery, null, ctx.ctx)
    const { user } = data
    const { network } = user

    const anomErrorCode = network?.[':yd-anom/error-code']
    if (anomErrorCode) {
      // Clojure keyword error codes serialize with a leading colon. The
      // contract-expired block UI keys off the bare 'contract-expired', so
      // normalize it here. The backend puts the viewing user's role on the
      // anomaly since the network node itself is replaced by the anomaly
      // when the check throws.
      if (
        anomErrorCode === ':contract-expired' ||
        anomErrorCode === 'contract-expired'
      ) {
        const anomData =
          network[':yd-anom/data'] ?? network['yd-anom/data'] ?? {}
        return {
          pageProps: {
            interception: {
              error: 'contract-expired',
              data: {
                role: anomData[':role'] ?? anomData.role
              }
            }
          }
        }
      }

      return {
        pageProps: {
          interception: {
            error: anomErrorCode
          }
        }
      }
    }
    const { query, asPath } = networkCtx.ctx

    // inspect here before fetching gIP
    if (query.id && !isValidSquuid(query.id as string)) {
      return {
        pageProps: {
          interception: {
            error: Number(query.id) ? 'is-legacy-eid' : 'not-found'
          }
        }
      }
    }

    const router = {
      queryParams: R.omit(['id'], query),
      pathname: path(asPath) || ''
    }

    const store = {
      db: treeToDb(graphQuery, data),
      user: R.omit(['board', 'network'], user),
      router,
      network: {
        ...network,
        name: networkCtx.ctx.networkName
      }
    }

    props = {
      pageProps: {
        ...store
      }
    }
  }

  // This call runs GIP inside each page
  const pageProps = await App.getInitialProps(
    networkCtx as unknown as AppContext
  )

  const pageError = R.path(['initialProps', 'error'], pageProps)

  if (pageError) {
    throw pageError
  }

  // TODO: this function is not working correctly it gives { pageProps, banner }
  // desired behavior is { pageProps: { banner } } but it crashes the app because incorrect msg
  // return R.mergeDeepLeft(
  //   R.mergeDeepLeft(browserWarningBanner(networkCtx.ctx.req) ?? {}, pageProps),
  //   props
  // )
  const result = R.mergeDeepLeft(pageProps, props)
  const flags: FeatureFlags = await fetchFeatureFlags(
    result.pageProps,
    networkName ?? ''
  )

  result.pageProps.flags = flags

  return result
}

function handleGetInitialPropsError(e: $TSFixMe, ctx: AppContext) {
  if (e.name === 'ApolloError') {
    return {
      pageProps: {
        interception: handleApolloError(e, ctx.ctx)
      }
    }
  }

  if (e.code && e.code === 'ECONNREFUSED') {
    const { address, port } = e
    return {
      pageProps: {
        interception: {
          error: 'connection-refused',
          cause: { address, port }
        }
      }
    }
  }

  const response = e.response

  let errorCode

  if (response && response.body && response.body.error) {
    errorCode = response.body.error
  } else if (response?.statusCode) {
    //  5xx errors most likely have no body
    errorCode = response.statusCode
  }

  if (errorCode) {
    // if body is absent, assume a 500 error
    const errorData = response?.body || { error: 'internal-server-error' }

    return {
      pageProps: {
        interception: handleErrorData(ctx.ctx, errorData)
      }
    }
  }

  logger.error(e, { source: 'getInitalProps' })

  // TODO
  // what should frontend do when we have an
  // uncaught exception during getInitialProps()?
  if (e.crossDomain && !e.status) {
    return {
      pageProps: {
        interception: {
          error: 'connection-refused'
        }
      }
    }
  }

  return {
    pageProps: {
      interception: {
        error: 'unknown'
      }
    }
  }
}

YDApp.getInitialProps = async (ctx: AppContext) => {
  try {
    // const networkName = getNetworkSubdomain(ctx.ctx)
    // const context = { ...DEFAULT_FLIPT_CONTEXT }
    // context.network_name = networkName || 'anonymous-network'

    // @TODO rename to fetchFeatureFlags and move to initialProps function
    // it should get user and network info from the getInitialProps function
    // const flags = await fetchFliptFlags(context)

    const client = initializeApollo({ ctx: ctx.ctx })
    const initialProps = await getInitialProps(ctx, client)

    if (!initialProps) {
      throw new Error('inital props empty')
    }

    // Apollo cache hydration removed to reduce initial payload size
    // Client-side queries will start with an empty cache and fetch data on mount
    return initialProps
  } catch (e: $TSFixMe) {
    return handleGetInitialPropsError(e, ctx)
  }
}

export default YDApp
